import { useCallback, useEffect, useState, type ReactNode } from "react";
import { AuthContext, type AuthState } from "./AuthContext";
import { endSession, loadSession, markInteraction, resumeAutomaticLogin, sendActivity, SessionError, startLogin, suppressAutomaticLogin, type User } from "./session";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try { const s = await loadSession(); setUser(s?.user ?? null); setError(previous => !s && previous === "Доступ к Recordings отозван." ? previous : null); }
    catch (err) {
      const denied = err instanceof SessionError && err.code === "access_revoked";
      if (denied) setUser(null);
      setError(denied ? "Доступ к Recordings отозван." : "Не удалось проверить доступ. Повторите попытку — сессия сохранена.");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    // Retire tokens from the old SPA. The BFF stores no OAuth credentials here.
    try { for (const key of Object.keys(sessionStorage)) if (key.startsWith("oidc.")) sessionStorage.removeItem(key); } catch { /* unavailable storage */ }
    void reload();
    const lost = (event: Event) => {
      setUser(null);
      if ((event as CustomEvent<string>).detail === "access_revoked") setError("Доступ к Recordings отозван.");
    };
    const visible = () => { if (!document.hidden) void reload(); };
    const storage = (event: StorageEvent) => { if (event.key === "recordings:logout") { suppressAutomaticLogin(); location.replace("/login?logged_out=1"); } };
    window.addEventListener("recordings:auth-lost", lost);
    window.addEventListener("storage", storage);
    window.addEventListener("pointerdown", markInteraction);
    window.addEventListener("keydown", markInteraction);
    document.addEventListener("visibilitychange", visible);
    const timer = setInterval(() => {
      void sendActivity().catch(() => undefined);
      if (!document.hidden) void reload();
    }, 60_000);
    return () => {
      clearInterval(timer);
      window.removeEventListener("recordings:auth-lost", lost);
      window.removeEventListener("storage", storage);
      window.removeEventListener("pointerdown", markInteraction);
      window.removeEventListener("keydown", markInteraction);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [reload]);
  const signin = async (): Promise<void> => { startLogin(); };
  const signout = async (): Promise<void> => {
    try {
      await endSession();
      try { localStorage.setItem("recordings:logout", String(Date.now())); } catch { /* next request still denies other tabs */ }
      setUser(null);
      location.replace("/login?logged_out=1");
    } catch { resumeAutomaticLogin(); setError("Не удалось завершить выход. Повторите попытку."); }
  };
  const state: AuthState = { user, loading, signin, signout, error, reload };
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
